import React from 'react';
import '../styled/pages/dashboard.scss';

interface PaginationProps {
  currentPage?: number;
  totalPages?: number;
  itemsPerPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const handlePrevPage = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };

  // Function to generate smart page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Total visible page numbers including ellipsis

    if (totalPages <= maxVisiblePages) {
      // If total pages is small, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 4) {
        // Show first few pages
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show last few pages
        pages.push('ellipsis');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show pages around current page
        pages.push('ellipsis');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="pagination">
      <div className="pagination__result">
        {itemsPerPage * (currentPage - 1) + 1}-{Math.min(itemsPerPage * currentPage, totalItems)} of{' '}
        {totalItems} items
      </div>
      <nav aria-label="pagination__navigation">
        <ul className="pagination__navigation__list">
          {/* Go to first page */}
          <li className="pagination__navigation__item">
            <button
              className="btnPrev"
              type="button"
              aria-label="Go to first page"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
            >
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
              >
                <path
                  d="M7.83333 11.3332L4.5 7.99984L7.83333 4.6665M12.5 11.3332L9.16667 7.99984L12.5 4.6665"
                  stroke="#1EA9EA"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              First
            </button>
          </li>

          {/* Go to previous page */}
          <li className="pagination__navigation__item">
            <button
              className="btnPrev"
              type="button"
              aria-label="Go to previous page"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
              >
                <path
                  d="M10.5 12L6.5 8L10.5 4"
                  stroke="#1EA9EA"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Prev
            </button>
          </li>

          {/* Smart page numbers with ellipsis */}
          {pageNumbers.map((page, index) => (
            <li
              className="pagination__navigation__item"
              key={index}
            >
              {page === 'ellipsis' ? (
                <span className="pagination__ellipsis">...</span>
              ) : (
                <button
                  className={`btn ${currentPage === page ? 'btn--active' : ''}`}
                  type="button"
                  onClick={() => onPageChange(page as number)}
                >
                  {page}
                </button>
              )}
            </li>
          ))}

          {/* Go to next page */}
          <li className="pagination__navigation__item">
            <button
              className="btnPrev"
              type="button"
              aria-label="Go to next page"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
              >
                <path
                  d="M6.5 12L10.5 8L6.5 4"
                  stroke="#1EA9EA"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </li>

          {/* Go to last page */}
          <li className="pagination__navigation__item">
            <button
              className="btnPrev"
              type="button"
              aria-label="Go to last page"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
              >
                <path
                  d="M8.66667 11.3332L12 7.99984L8.66667 4.6665M4 11.3332L7.33333 7.99984L4 4.6665"
                  stroke="#1EA9EA"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </li>
        </ul>
      </nav>

      {/* Page selector */}
      <div className="pagination__actions max-h-[40px]">
        <select
          className="formField__select"
          value={currentPage}
          onChange={e => onPageChange(Number(e.target.value))}
        >
          {Array.from({ length: totalPages }).map((_, index) => (
            <option
              key={index}
              value={index + 1}
            >
              {index + 1}
            </option>
          ))}
        </select>
        <h6>Go to page</h6>
      </div>

      {/* <div className="pagination__itemsPerPage">
        <select
          className="formField__select"
          value={itemsPerPage}
          onChange={e => onItemsPerPageChange(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map(size => (
            <option
              key={size}
              value={size}
            >
              {size} items per page
            </option>
          ))}
        </select>
      </div> */}
    </div>
  );
};

export default Pagination;
